/** /j/<code> — the QR target. One field (name or seat), one button, no account. In `home`
 * mode it is also the Student app's landing (Home.tsx): a student-first screen with the
 * product name and the teacher door as a footnote. */
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { capabilities } from "../platform/capabilities";
import { candidateSubnets, findRooms, type Room } from "../platform/discovery";
import {
  bundledOrigin,
  forgetServer,
  handOff,
  hostOf,
  normaliseOrigin,
  originFromLink,
  probeServer,
  rememberServer,
  serverOrigin,
} from "../platform/serverOrigin";
import { useSurface } from "../platform/surface";
import { ApiError, post } from "../api/client";
import { setLang, type Lang } from "../i18n";
import { deviceId } from "../live/device";
import { lastSignups, noteRegistration, registrationFor } from "../live/studentIdentity";
import { lastJoin, rememberJoin, type Joined } from "../live/studentSession";
import { BottomNav } from "../student/BottomNav";
import { SignupFootnote } from "../student/SignupFootnote";
import { product } from "../product";
import { Button } from "../ui";

const CODE_IN_TEXT = (text: string): string | null => {
  const m = /\/j\/([A-Za-z0-9]{4,8})/.exec(text) ?? /^([A-Za-z0-9]{4,8})$/.exec(text.trim());
  return m ? m[1]!.toUpperCase() : null;
};

export function Join({ home = false }: { home?: boolean } = {}) {
  // The student app wears the theme, not the screen (src/platform/surface.ts): this screen is
  // white in light and the board green in dark, and the status bar above it is the same.
  useSurface("student");
  const { code: initialCode } = useParams({ strict: false }) as { code?: string };
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  /** The sitting to offer back. Shown on the app landing, and on a bare `/j` — which is where
   * the "another lesson" door in the lesson header lands, so leaving a lesson to look for
   * another one is reversible in one tap. Never on a scanned `/j/<code>`: that student was
   * sent to a specific lesson and the screen keeps its one job. */
  const last = useMemo(() => (home || !initialCode ? lastJoin() : null), [home, initialCode]);
  /** STORY-113: the sitting this device knows about, whether or not this is the app landing:
   * it carries the session the sign-up would attach to and the policy that allows it. */
  const sitting = useMemo(() => lastJoin(), []);
  const other: Lang = i18n.language.startsWith("ru") ? "en" : "ru";
  const [nfcBusy, setNfcBusy] = useState(false);
  const [code, setCode] = useState((initialCode ?? "").toUpperCase());
  const [label, setLabel] = useState(() => new URLSearchParams(location.search).get("label") ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // STORY-110: the bundled copy inside the shell needs to know the class server first
  const bundled = useMemo(() => bundledOrigin(), []);
  const [server, setServer] = useState<string | null>(() => (bundled ? serverOrigin() : null));
  const [serverInput, setServerInput] = useState("");
  const [serverBusy, setServerBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [editingServer, setEditingServer] = useState(false);
  const askServer = bundled && (!server || editingServer);
  const [rooms, setRooms] = useState<Room[] | null>(null);
  const [scanning, setScanning] = useState<number | null>(null);

  /** STORY-111: look for a Teacher Computer app on this Wi-Fi instead of typing its address. */
  async function scanRooms() {
    setScanning(0);
    setRooms(null);
    setServerError(null);
    try {
      const subnets = await candidateSubnets(server ?? serverOrigin());
      setRooms(await findRooms(subnets, { onProgress: setScanning }));
    } finally {
      setScanning(null);
    }
  }

  function chooseRoom(room: Room) {
    rememberServer(room.origin);
    setServer(room.origin);
    setServerInput(room.origin);
    setRooms(null);
    setEditingServer(false);
  }

  async function saveServer(): Promise<string | null> {
    const origin = normaliseOrigin(serverInput);
    setServerError(null);
    if (!origin) {
      setServerError(t("join.serverBad"));
      return null;
    }
    setServerBusy(true);
    try {
      if (!(await probeServer(origin))) {
        setServerError(t("join.serverBad"));
        return null;
      }
      rememberServer(origin);
      setServer(origin);
      setEditingServer(false);
      return origin;
    } finally {
      setServerBusy(false);
    }
  }

  /** A scanned or tapped link carries the server: adopt it and go straight there. */
  function adoptLink(text: string): boolean {
    const origin = originFromLink(text);
    const found = CODE_IN_TEXT(text);
    if (!origin) return false;
    rememberServer(origin);
    setServer(origin);
    if (found) handOff(origin, `/j/${found}`);
    return true;
  }

  async function join() {
    setBusy(true);
    setError(null);
    if (bundled) {
      const origin = server ?? (serverInput ? await saveServer() : null);
      setBusy(false);
      if (!origin) {
        setEditingServer(true);
        setError(t("join.serverNeeded"));
        return;
      }
      const q = label.trim() ? `?label=${encodeURIComponent(label.trim())}` : "";
      handOff(origin, `/j/${code.trim().toUpperCase()}${q}`);
      return;
    }
    try {
      const j = await post<Joined & { rejoined: boolean; signups?: string; registration_status?: string }>("/session/v1/sessions/join", {
        code: code.trim().toUpperCase(),
        device_id: deviceId("student"),
        label: label.trim() || undefined,
      });
      rememberJoin(j);
      // STORY-113: the join carries both the course policy and what this teacher's system
      // already holds for this device, so the footnote knows whether to exist and what to say
      noteRegistration(j.session_id, j.signups, j.registration_status);
      void navigate({ to: "/s/$sessionId", params: { sessionId: j.session_id } });
    } catch (e) {
      const codeName = e instanceof ApiError ? e.code : undefined;
      setError(t(`join.errors.${codeName ?? "generic"}`, { defaultValue: t("join.errors.generic") }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="font-system flex min-h-dvh flex-col justify-center gap-6 overflow-x-clip p-6">
      <header className="mx-auto w-full max-w-md">
        {home ? (
          <>
            <h1 className="wrap-anywhere text-2xl font-bold tracking-tight">{t("join.homeTitle")}</h1>
            <p className="mt-1 text-lg text-[color:var(--ink-2)]">{t("join.homeHint")}</p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold tracking-tight" translate="no">{product.name}</h1>
            <p className="mt-1 text-lg text-[color:var(--ink-2)]">{t("join.subtitle")}</p>
          </>
        )}
      </header>
      {last && (
        <section className="mx-auto w-full max-w-md rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
          <p className="text-sm">{t("join.resumeHint")}</p>
          <Button type="button" variant="secondary" block className="mt-3" onClick={() => void navigate({ to: "/s/$sessionId", params: { sessionId: last.session_id } })}>
            {t("join.resume")}
          </Button>
        </section>
      )}
      <form
        className="mx-auto flex w-full max-w-md flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void join();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("join.code")}</span>
          <input
            className="field min-h-16 w-full min-w-0 text-3xl font-bold tracking-[0.25em] uppercase"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            name="code"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            maxLength={8}
            required
          />
        </label>
        {capabilities.codeScanner && (
          <Button
            type="button"
            variant="secondary"
            block
            onClick={async () => {
              const text = await capabilities.codeScanner!.scan();
              if (text && bundled && adoptLink(text)) return;
              const found = text ? CODE_IN_TEXT(text) : null;
              if (found) setCode(found);
            }}
          >
            {t("join.scan")}
          </Button>
        )}
        {capabilities.nfc && (
          <Button
            type="button"
            variant="secondary"
            block
            disabled={nfcBusy}
            onClick={async () => {
              setNfcBusy(true);
              try {
                const text = await capabilities.nfc!.read();
                if (text && bundled && adoptLink(text)) return;
                const found = text ? CODE_IN_TEXT(text) : null;
                if (found) setCode(found);
              } finally {
                setNfcBusy(false);
              }
            }}
          >
            {nfcBusy ? t("join.nfcWaiting") : t("join.nfc")}
          </Button>
        )}
        {askServer && (
          <section className="flex flex-col gap-2 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
            <h2 className="text-base font-semibold">{t("join.serverTitle")}</h2>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium">{t("join.serverLabel")}</span>
              <input
                className="field min-h-14 text-lg"
                name="server"
                value={serverInput}
                onChange={(e) => setServerInput(e.target.value)}
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t("join.serverExample")}
              />
            </label>
            <p className="text-sm text-[color:var(--ink-2)]">{t("join.serverHint")}</p>
            {serverError && (
              <p role="alert" className="text-sm" style={{ color: "var(--not-yet)" }}>
                {serverError}
              </p>
            )}
            <Button type="button" variant="secondary" block loading={serverBusy} disabled={!serverInput.trim()} onClick={() => void saveServer()}>
              {t("join.serverSave")}
            </Button>
            <Button type="button" variant="tertiary" block loading={scanning !== null} onClick={() => void scanRooms()}>
              {scanning === null ? t("join.findRoom") : t("join.findRoomBusy", { pct: Math.round(scanning * 100) })}
            </Button>
            {rooms && rooms.length > 0 && (
              <ul className="flex flex-col gap-2" aria-label={t("join.roomsFound")}>
                {rooms.map((room) => (
                  <li key={room.device_id}>
                    <button
                      type="button"
                      className="flex min-h-[var(--control-h)] w-full items-center justify-between gap-3 rounded-[var(--radius-control)] border border-[color:var(--line)] px-3 text-left"
                      onClick={() => chooseRoom(room)}
                    >
                      <span className="min-w-0 truncate font-medium">{room.room}</span>
                      <span className="shrink-0 text-xs text-[color:var(--ink-2)]" translate="no">{hostOf(room.origin)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {rooms && rooms.length === 0 && <p className="text-sm text-[color:var(--ink-2)]">{t("join.noRooms")}</p>}
          </section>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("join.label")}</span>
          <input
            className="field min-h-14 text-lg"
            name="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoComplete="nickname"
            maxLength={40}
            placeholder={t("join.labelHint")}
          />
        </label>
        {error && (
          <p role="alert" className="rounded-[var(--radius-control)] bg-[color:var(--surface)] p-3 shadow-sm" style={{ color: "var(--not-yet)" }}>
            {error}
          </p>
        )}
        <Button type="submit" block loading={busy} disabled={code.trim().length < 4}>
          {t("join.cta")}
        </Button>
      </form>
      {/* STORY-113: one line, under the join screen, only where the teacher allows sign-ups.
          The policy is whatever this device was last told; a device that has never joined
          knows nothing and therefore offers nothing. */}
      {sitting && <SignupFootnote policy={lastSignups()} sessionId={sitting.session_id} status={registrationFor(sitting.session_id)?.status ?? "none"} />}
      {home && (
        <footer className="mx-auto flex w-full max-w-md flex-col gap-1 text-sm text-[color:var(--ink-2)]">
          <div className="flex items-center justify-between gap-3">
            <span translate="no">{product.name}</span>
            <button
              type="button"
              lang={other}
              className="inline-flex min-h-[var(--control-h)] items-center px-3 underline"
              aria-label={`${t("app.language")}: ${t(`app.langNames.${other}`)}`}
              onClick={() => setLang(other)}
            >
              {t(`app.langNames.${other}`)}
            </button>
          </div>
          {bundled && server && !editingServer && (
            <p className="flex flex-wrap items-center gap-x-2">
              <span className="wrap-anywhere">{t("join.serverCurrent", { host: hostOf(server) })}</span>
              <button
                type="button"
                className="inline-flex min-h-[var(--control-h)] items-center underline"
                onClick={() => {
                  setServerInput(server);
                  setEditingServer(true);
                  forgetServer();
                }}
              >
                {t("join.serverChange")}
              </button>
            </p>
          )}
          <p>
            {t("join.teacherNote")}{" "}
            <Link to="/signin" className="inline-flex min-h-[var(--control-h)] items-center underline">
              {t("join.teacherLink")}
            </Link>
          </p>
        </footer>
      )}
      <BottomNav />
    </main>
  );
}
